import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthCtx = createContext(null);

/**
 * Auth uses httpOnly cookies. We do NOT persist tokens in localStorage.
 * On mount we call /auth/me — the browser sends the cookie automatically.
 */
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null=checking, false=guest, obj=authed
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            console.warn("[auth] no active session", e?.response?.status);
            setUser(false);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const login = useCallback(async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setUser(data.user);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    }, []);

    const register = useCallback(async (payload) => {
        try {
            const { data } = await api.post("/auth/register", payload);
            setUser(data.user);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    }, []);

    const logout = useCallback(async () => {
        try { await api.post("/auth/logout"); }
        catch (e) { console.warn("[auth] logout error", e); }
        setUser(false);
    }, []);

    const refreshUser = useCallback(async () => {
        try { const { data } = await api.get("/auth/me"); setUser(data); }
        catch (e) { console.warn("[auth] refresh failed", e); }
    }, []);

    const value = useMemo(
        () => ({ user, loading, login, register, logout, refreshUser, setUser }),
        [user, loading, login, register, logout, refreshUser]
    );

    return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
